import {
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  collection,
  serverTimestamp,
} from 'firebase/firestore';

const FAVORITES_CACHE_KEY = 'ff_favorites';

/**
 * Favorites Service
 * - Firestore is source of truth: /users/{uid}/favorites/{storeId}
 * - localStorage is client cache for instant UI
 *
 * NOTE: db (Firestore instance) is passed as parameter to avoid top-level
 * getFirestore() call before Firebase app is initialized.
 */

// --- Firestore operations ---

export const addFavoriteToFirestore = async (db, uid, storeId, storeData) => {
  const ref = doc(db, 'users', uid, 'favorites', storeId);
  await setDoc(ref, {
    storeId,
    storeName: storeData.locationName || storeData.storeName || '',
    logoUrl: storeData.logoUrl || storeData.design?.logoUrl || '',
    address: storeData.address || storeData.city || '',
    latitude: storeData.latitude || null,
    longitude: storeData.longitude || null,
    addedAt: serverTimestamp(),
  });
};

export const removeFavoriteFromFirestore = async (db, uid, storeId) => {
  const ref = doc(db, 'users', uid, 'favorites', storeId);
  await deleteDoc(ref);
};

export const getFavoritesFromFirestore = async (db, uid) => {
  const snap = await getDocs(collection(db, 'users', uid, 'favorites'));
  const list = [];
  snap.docs.forEach((d) => {
    const data = d.data();
    list.push({
      storeId: d.id,
      storeName: data.storeName || '',
      logoUrl: data.logoUrl || '',
      address: data.address || '',
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      addedAt: data.addedAt,
    });
  });
  return list;
};

// --- localStorage cache operations ---

export const getCachedFavorites = () => {
  try {
    const raw = localStorage.getItem(FAVORITES_CACHE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return [];
};

export const setCachedFavorites = (favorites) => {
  try {
    localStorage.setItem(FAVORITES_CACHE_KEY, JSON.stringify(favorites));
  } catch (e) {
    console.error('Failed to cache favorites:', e);
  }
};

export const clearCachedFavorites = () => {
  try {
    localStorage.removeItem(FAVORITES_CACHE_KEY);
  } catch (e) {
    console.error('Failed to clear favorites cache:', e);
  }
};

// --- High-level helpers ---

export const toggleFavorite = async (db, uid, storeId, storeData, currentList) => {
  const isFav = currentList.some((f) => f.storeId === storeId);
  if (isFav) {
    // Remove
    await removeFavoriteFromFirestore(db, uid, storeId);
    const next = currentList.filter((f) => f.storeId !== storeId);
    setCachedFavorites(next);
    return { list: next, isFavorite: false };
  }
  // Add
  await addFavoriteToFirestore(db, uid, storeId, storeData);
  const item = {
    storeId,
    storeName: storeData.locationName || storeData.storeName || '',
    logoUrl: storeData.logoUrl || storeData.design?.logoUrl || '',
    address: storeData.address || storeData.city || '',
    latitude: storeData.latitude || null,
    longitude: storeData.longitude || null,
  };
  const next = [...currentList, item];
  setCachedFavorites(next);
  return { list: next, isFavorite: true };
};

export const loadAndCacheFavorites = async (db, uid) => {
  const list = await getFavoritesFromFirestore(db, uid);
  setCachedFavorites(list);
  return list;
};

export const isFavoriteCached = (storeId) => {
  const list = getCachedFavorites();
  return list.some((f) => f.storeId === storeId);
};
